<script lang="ts">
  import { navigate } from '../router.svelte';
  import { memberStatusMeta, timeAgo } from '../admin';
  import { fullDate } from '../revenue';
  import { humanize, isPinned, whyReadings } from '../pipelines';
  import {
    fetchCardHistory,
    fetchPipelineCard,
    queueDirectMessage,
    type CardTransition,
    type PipelineCard,
    type PipelineStage,
  } from '@api';
  import { askHelperToSend } from '../helper.svelte';
  import ReplyComposer from '@ui/composer/ReplyComposer.svelte';
  import SkoolLink from '@ui/SkoolLink.svelte';
  import { skoolProfileUrl } from '@ui/member';
  import type { DmMessageView } from '@ui/conversation/types';
  import Avatar from '@ui/Avatar.svelte';
  import Drawer from './Drawer.svelte';
  import MemberActivity from './MemberActivity.svelte';
  import MemberConversation from './MemberConversation.svelte';
  import StageBadge from './StageBadge.svelte';
  import PlanCell from '@ui/PlanCell.svelte';
  import RenewalCell from '@ui/RenewalCell.svelte';

  // One card's detail drawer, reusing the shared right-side `Drawer`. Seeded with the
  // card the board already holds and refreshed from `GET …/cards/{id}` on open so the
  // history/timestamps are authoritative. The stage <select> is the move affordance
  // that doesn't need a drag; the outreach link points at where drafts are reviewed.
  let {
    skoolId,
    pipelineName,
    stages,
    card: initial,
    nowMs = Date.now(),
    onMove,
    onUnpin,
    onNote,
    onClose,
  }: {
    skoolId: number;
    pipelineName: string;
    stages: PipelineStage[];
    card: PipelineCard;
    nowMs?: number;
    onMove?: ((cardId: number, stageKey: string) => void) | undefined;
    /** Release the 7-day manual pin early. The page prompts for a reason first — this
     *  only raises the intent, exactly as `onMove` does. */
    onUnpin?: ((cardId: number) => void) | undefined;
    /** Save the owner's working note. Unlike a move this needs no reason and raises no
     *  prompt — the note IS the reason, and a dialog in front of a textarea is a dialog
     *  that stops it being written. Returns the saved card so the drawer can adopt the
     *  server's stamp rather than guess it. */
    onNote?: ((cardId: number, note: string | null) => Promise<PipelineCard>) | undefined;
    onClose?: () => void;
  } = $props();

  // The board's copy renders immediately; `GET …/cards/{id}` — the endpoint the drawer
  // is for — refreshes it to the authoritative detail on open (and on card switch).
  // `card` is derived so the fetched copy simply supersedes the prop, no re-seeding.
  let fetched = $state<PipelineCard | null>(null);
  const card = $derived(fetched && fetched.card_id === initial.card_id ? fetched : initial);
  $effect(() => {
    const id = initial.card_id;
    const key = initial.pipeline_key;
    let alive = true;
    fetched = null;
    fetchPipelineCard(skoolId, key, id)
      .then((fresh) => {
        if (alive && fresh.card_id === id) fetched = fresh;
      })
      .catch(() => {
        /* keep the board's copy — the drawer already renders it */
      });
    return () => {
      alive = false;
    };
  });

  const m = $derived(card.member);
  const name = $derived(m?.name ?? 'Unknown member');
  const status = $derived(memberStatusMeta(m?.status));
  const pinned = $derived(isPinned(card, nowMs));
  const priced = $derived((m?.plan_amount_cents ?? 0) > 0 || !!m?.plan_tier);

  function onStageChange(e: Event): void {
    const el = e.currentTarget as HTMLSelectElement;
    const next = el.value;
    // SNAP BACK IMMEDIATELY. Choosing a state only RAISES the move — the page prompts for
    // a reason and the card may never move at all (cancelled, or refused). The card is
    // the source of truth, so the select re-renders from it once the move actually lands;
    // leaving it showing the pending choice would have it lie for as long as the prompt
    // is open, and permanently if the answer is no.
    el.value = card.stage_key;
    if (next) onMove?.(card.card_id, next);
  }

  // The card's real journey. Cards are permanent and hold only the CURRENT state, so
  // every move is recorded in `crm_card_transitions` and read here — this is the payoff
  // of the state model, and the only place the member's path is visible.
  let transitions = $state<CardTransition[]>([]);
  let transitionsTotal = $state(0);
  $effect(() => {
    const id = initial.card_id;
    const key = initial.pipeline_key;
    let alive = true;
    transitions = [];
    transitionsTotal = 0;
    fetchCardHistory(skoolId, key, id)
      .then((h) => {
        if (!alive) return;
        transitions = h.items;
        transitionsTotal = h.total;
      })
      .catch(() => {
        /* the timestamps below still render — history is additive */
      });
    return () => {
      alive = false;
    };
  });

  /** A state key → its label, so the journey reads in the board's own words. */
  function label(key: string | null): string {
    if (!key) return '—';
    return stages.find((st) => st.key === key)?.label ?? humanize(key);
  }

  /** `system` is the resolver; anything else is an admin user id. */
  function actorLabel(actor: string): string {
    return actor === 'system' ? 'auto' : 'by hand';
  }

  // The timestamp rows worth showing, in order, dropping the ones a given card lacks.
  //
  // "Last outreach" / "Last reply" used to sit between the two below. They were dropped
  // with the columns behind them (`f7b2d94c1e08`): nothing had ever written either, so
  // the pair advertised a touchpoint history the card did not have. The real one is the
  // DM thread further down this drawer, which reads the mirrored conversation off the
  // MEMBER — where an outreach and a reply actually live.
  //
  // "Member last active" went the same way in `a3f81c7d2b46`: its column was stamped
  // only by the win-back plan generator, so it froze at whenever a plan was last
  // generated rather than tracking the member. `m.last_active` in the row above is the
  // live one.
  /**
   * WHICH QUESTION THE READER IS ASKING, one at a time.
   *
   * All of this used to be one column: stage, billing, the journey, the timestamps, the
   * DM transcript and the activity feed, stacked. Answering somebody meant scrolling
   * past the audit trail to reach them, and the audit trail is the part nobody opens a
   * card for. Three panes, each a real question — what have we said, what are they
   * doing, how did this card get here — and the one you open on is the one with an
   * action in it.
   */
  // --- the owner's note -------------------------------------------------------------
  //
  // Edited in place rather than behind a modal: this is the field people are meant to
  // reach for constantly, and every click between "I learned something" and "it is
  // written down" is a reason the CRM goes stale.
  let noteDraft = $state('');
  let noteEditing = $state(false);
  let noteBusy = $state(false);
  let noteError = $state<string | null>(null);
  /** What they answered at the door. Detail-only, so it arrives with the drawer's own
   *  `GET …/cards/{id}` rather than with the board — `card` is derived from that fetch,
   *  so this fills in a moment after open. */
  const survey = $derived(card.member?.survey ?? []);
  /** Skool blanks an `email` answer for anyone below admin. The question still arrives,
   *  so the pane can say WHY it is empty instead of rendering a silent blank that reads
   *  as "they left it blank". */
  const emailWithheld = $derived(
    survey.some((a) => a.type === 'email' && !a.answer),
  );

  const noteSaved = $derived(card.note ?? '');
  const noteDirty = $derived(noteDraft.trim() !== noteSaved.trim());

  function startNote(): void {
    noteDraft = noteSaved;
    noteError = null;
    noteEditing = true;
  }

  async function saveNote(): Promise<void> {
    if (!onNote || noteBusy) return;
    noteBusy = true;
    noteError = null;
    try {
      // Blank -> null. An empty note is the ABSENCE of a note, not a note that says
      // nothing, and the server stores it that way too.
      const saved = await onNote(card.card_id, noteDraft.trim() || null);
      fetched = saved;
      noteEditing = false;
    } catch (err: unknown) {
      noteError = err instanceof Error ? err.message : 'That did not save.';
    } finally {
      noteBusy = false;
    }
  }

  type Tab = 'talk' | 'intake' | 'activity' | 'history';
  let tab = $state<Tab>('talk');
  // DERIVED, NOT CONST. `survey` arrives with the drawer's own `GET …/cards/{id}`, a
  // moment after open — a `const` list is built once, while it is still empty, and the
  // Joined tab would never appear at all.
  const TABS: { key: Tab; label: string }[] = $derived([
    { key: 'talk' as Tab, label: 'Conversation' },
    // Between the conversation and the behavioural panes on purpose: what someone said
    // at the door is the oldest thing you know about them, and the thing you want in
    // front of you before writing the first message. Offered only when the community
    // actually asks join questions — an empty tab promises content that is not coming.
    // "Intake Form", not "Joined": the tab holds the form they filled in at the door,
    // and `joined` reads as a DATE — which the header already shows.
    //
    // OFFERED ONLY WHEN THERE ARE ANSWERS. Most communities ask nothing, and a tab that
    // opens on an empty pane is a promise of content that is not coming.
    ...(survey.length ? [{ key: 'intake' as Tab, label: 'Intake Form' }] : []),
    { key: 'activity' as Tab, label: 'Activity' },
    { key: 'history' as Tab, label: 'History' },
  ]);

  /**
   * Send a DM the same way this dashboard sends everything else: onto the outbox, then
   * wake the Helper holding the Skool session.
   *
   * `askHelperToSend` returns the word the composer ticks — "Sent" only when the drain
   * reported nothing failed, "Queued" otherwise, which stays true when there is no
   * Helper in this browser at all.
   */
  /**
   * Messages sent from this drawer, held until the mirror has them.
   *
   * Cleared when the card changes: they belong to one conversation, and carrying them
   * onto the next member's would put words in a stranger's thread.
   */
  let pending = $state<DmMessageView[]>([]);
  $effect(() => {
    void initial.card_id;
    pending = [];
  });

  async function sendDm(text: string): Promise<string> {
    await queueDirectMessage(skoolId, card.member_id, text);
    // ONLY AFTER THE ROW EXISTS. Painting first and reconciling on failure would show
    // the owner a message that was never queued — and `queueDirectMessage` throwing is
    // exactly the case the composer surfaces, so an optimistic bubble above its own
    // error would be the screen contradicting itself.
    pending = [
      ...pending,
      {
        id: `pending:${Date.now()}`,
        content: text,
        createdAt: new Date().toISOString(),
        outbound: true,
        videos: null,
        attachments: null,
      },
    ];
    return await askHelperToSend();
  }

  const timestamps = $derived(
    (
      [
        ['Entered', card.entered_at],
        ['Stage changed', card.stage_changed_at],
      ] as [string, string | null][]
    ).filter((row): row is [string, string] => row[1] != null),
  );
</script>

<Drawer open scroll={false} onclose={() => onClose?.()} label="Pipeline card">
  <!-- Header -->
  <div class="flex items-center gap-3 border-b hair px-5 py-3.5">
    <Avatar src={m?.picture_profile ?? null} {name} size={38} memberId={m ? card.member_id : null} />
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-2">
        <button
          class="truncate text-left text-sm font-semibold enabled:hover:underline disabled:cursor-default"
          onclick={() => m && navigate(`/admin/community/members/${card.member_id}`)}
          disabled={!m}
        ><span class="redact">{name}</span></button>
        {#if m?.stage}<StageBadge stage={m.stage} momentum={m.momentum} />{/if}
      </div>
      <div class="eyebrow flex items-center gap-2 text-base-content/65">
        <!-- privacy-ok: the pipeline's name — a board this owner defined, not a person -->
        <span class="truncate">{pipelineName}</span>
        <!-- Their actual profile. The drawer names somebody and says a lot ABOUT them;
             the next thing anyone wants is to go and look at them on Skool. -->
        <!-- privacy-ok: the handle is only ever a URL here, never text; the title is stripped at runtime -->
        <SkoolLink href={skoolProfileUrl(m?.handle)} title="Open {name} on Skool" />
      </div>
    </div>
    <button
      class="rounded-field p-1.5 text-base-content/65 transition-colors hover:bg-base-200 hover:text-base-content"
      onclick={() => onClose?.()}
      aria-label="Close"
    >
      <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
    </button>
  </div>

  <!-- THE MOVE, AND WHAT THIS PERSON IS WORTH — both pinned, because both are true of
       every pane below and neither is what you scroll for. -->
  <div class="shrink-0 space-y-2.5 border-b hair px-5 py-3">
    <div class="flex flex-wrap items-center gap-2">
      <span class="eyebrow text-base-content/60">Stage</span>
      <select
        class="select select-bordered select-sm bg-base-100"
        value={card.stage_key}
        onchange={onStageChange}
        aria-label="Move this card to another stage"
        disabled={!onMove}
      >
        {#each stages as st (st.key)}
          <option value={st.key}>{st.label}</option>
        {/each}
      </select>
      {#if pinned}
        <span class="inline-flex items-center gap-1 rounded-field bg-primary/10 px-1.5 py-0.5 text-[0.62rem] font-medium text-primary" title="Moved by hand — the engine won't reroute it while pinned">
          <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14l-1.7-3.4a2 2 0 0 1-.3-1V5a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7.6a2 2 0 0 1-.3 1L5 17z" /></svg>
          pinned{#if card.pinned_until} · until {fullDate(card.pinned_until)}{/if}
        </span>
        {#if onUnpin}
          <!-- Only offered while the pin is LIVE. An expired pin needs no releasing, and
               the server answers `409 not_pinned` to prove the view was stale. -->
          <button
            class="rounded-field px-1.5 py-0.5 text-[0.62rem] font-medium text-base-content/60 underline-offset-2 transition-colors hover:text-primary hover:underline"
            onclick={() => onUnpin(card.card_id)}
            title="Hand this card back to the resolver before the 7 days are up"
          >release</button>
        {/if}
      {/if}
      {#if status?.departed}
        <span class="rounded-field bg-base-content/8 px-1.5 py-0.5 text-[0.62rem] font-medium text-base-content/62">{status.label}</span>
      {/if}
    </div>

    <!-- Billing pair -->
    {#if priced || m?.ends_at}
      <div class="flex items-center gap-6 rounded-box border hair bg-base-100 px-4 py-3">
        <div>
          <div class="eyebrow mb-1 text-base-content/55">Plan</div>
          {#if m}<PlanCell plan={m} align="left" />{:else}<span class="text-sm text-base-content/40">—</span>{/if}
        </div>
        <div>
          <div class="eyebrow mb-1 text-base-content/55">Renewal</div>
          {#if m}<RenewalCell row={m} {nowMs} align="left" />{:else}<span class="text-sm text-base-content/40">—</span>{/if}
        </div>
      </div>
    {/if}
  </div>

  <!-- One question at a time. -->
  <div role="tablist" class="flex shrink-0 gap-1 border-b hair px-3">
    {#each TABS as t (t.key)}
      <button
        role="tab"
        aria-selected={tab === t.key}
        class="relative px-2.5 py-2 text-xs font-medium transition-colors
               {tab === t.key ? 'text-base-content' : 'text-base-content/55 hover:text-base-content/80'}"
        onclick={() => (tab = t.key)}
      >
        {t.label}
        {#if tab === t.key}
          <span class="absolute inset-x-2.5 -bottom-px h-0.5 rounded-full bg-primary"></span>
        {/if}
      </button>
    {/each}
  </div>

  <!-- THE ONLY THING THAT SCROLLS. `min-h-0` is load-bearing: without it a flex child
       sizes to its content and pushes the composer off the bottom instead of scrolling
       inside its share of the panel. -->
  <!-- The Conversation pane brings its own scroll — `<ConversationThread>` keeps the
       composer pinned and the newest message in view — so it gets the height rather
       than a scrollbar inside a scrollbar. The other two are ordinary documents. -->
  <div class="flex min-h-0 flex-1 flex-col {tab === 'talk' ? 'overflow-hidden px-5 py-3' : 'overflow-y-auto px-5 py-4'}">
    {#if tab === 'talk'}
      {#if m?.member_id != null}
        <MemberConversation
          {skoolId}
          memberId={m.member_id}
          memberName={name}
          {pending}
          {composer}
        />
      {:else}
        <p class="text-xs text-base-content/55">No member on this card to write to.</p>
      {/if}
    {:else if tab === 'intake'}
      <!-- A TABLE, not a stack of paragraphs. These are short question/answer pairs read
           by someone scanning for one fact ("did they ask for a call?"), and a two-column
           grid puts every answer on the same left edge so the eye runs down them. -->
      <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-x-4 text-sm">
        {#each survey as a, i (a.question)}
          <!-- privacy-ok: the QUESTION is the owner's own join-form copy, identical for
               every member of the community. The ANSWER carries the mark. -->
          <div class="border-t hair py-2 pr-2 text-xs leading-snug text-base-content/62 {i === 0 ? 'border-t-0' : ''}">
            {a.question}
          </div>
          <div class="border-t hair py-2 {i === 0 ? 'border-t-0' : ''}">
            {#if a.answer}
              <span class="redact whitespace-pre-wrap leading-snug">{a.answer}</span>
            {:else if a.type === 'email'}
              <span class="text-base-content/45 italic">Hidden from moderators</span>
            {:else}
              <span class="text-base-content/45 italic">Not answered</span>
            {/if}
          </div>
        {/each}
      </div>
      {#if emailWithheld}
        <!-- Actionable, not decorative: a one-word change on the owner's side, and the
             difference between holding their members' addresses and not. Proven, not
             guessed — 87 of 87 email answers arrive where we hold admin, 0 of 360 across
             two communities where we are only a moderator. -->
        <p class="hair mt-3 rounded-field border bg-base-200/40 px-2.5 py-2 text-[0.7rem] leading-relaxed text-base-content/62">
          Make our account an <strong>admin</strong> rather than a moderator and Skool
          releases the email answers — it withholds them at moderator level.
        </p>
      {/if}
    {:else if tab === 'activity'}
      {#if m?.member_id != null}
        <MemberActivity {skoolId} memberId={m.member_id} layout="stacked" pageSize={5} {nowMs} canReply />
      {:else}
        <p class="text-xs text-base-content/55">No member on this card.</p>
      {/if}
    {:else}
      <div class="space-y-5">
        <!-- Journey — the recorded state changes. The oldest row has no `from_state`:
        that is the member entering the pipeline, not a move. -->
        {#if transitions.length}
        <div>
        <div class="eyebrow mb-1.5 flex items-baseline justify-between text-base-content/60">
        <span>Journey</span>
        {#if transitionsTotal > transitions.length}
        <span class="font-mono text-[0.62rem] normal-case tracking-normal text-base-content/45">
        latest {transitions.length} of {transitionsTotal}
        </span>
        {/if}
        </div>
        <ol class="space-y-1.5 border-l hair pl-3">
        {#each transitions as t, i (i)}
        <li class="relative text-xs">
        <span class="absolute -left-[0.9rem] top-1.5 h-1.5 w-1.5 rounded-full {i === 0 ? 'bg-primary' : 'bg-base-content/25'}"></span>
        <div class="flex items-baseline justify-between gap-3">
        <span class="min-w-0 text-base-content/80">
        <!-- `pin` and `unpin` carry from_state === to_state, so the arrow form
        would read as "Joined → Joined". They are decisions ABOUT the
        state, not changes of it, and say so. -->
        {#if t.event === 'pin'}
        Pinned to <span class="font-medium">{label(t.to_state)}</span>
        {:else if t.event === 'unpin'}
        Pin released
        {:else if t.from_state}
        <span class="text-base-content/55">{label(t.from_state)}</span>
        <span class="text-base-content/35"> → </span>{label(t.to_state)}
        {:else}
        Entered as <span class="font-medium">{label(t.to_state)}</span>
        {/if}
        </span>
        {#if t.occurred_at}
        <span class="shrink-0 font-mono tabular-nums text-base-content/60" title={fullDate(t.occurred_at)}>
        {timeAgo(t.occurred_at, nowMs)}
        </span>
        {/if}
        </div>
        <span class="text-[0.62rem] text-base-content/40">{actorLabel(t.actor)}</span>
        {#if t.reason}
        <!-- The whole point of the prompt: the override is only reviewable later
        if the words that justified it sit next to it. -->
        <p class="mt-1 whitespace-pre-wrap rounded-field bg-base-200/50 px-2 py-1 text-[0.7rem] leading-snug text-base-content/78">
        {t.reason}
        </p>
        {/if}
        {#if t.why}
        <!-- THE MACHINE'S REASON, and the mirror of `reason` above: a human move is
        explained by the words of whoever made it, an engine move only by the state
        that claimed the member and what it READ to claim them. Without the readings
        this is unanswerable after the fact — the inputs are overwritten on every
        pass, so the values that justified the move are gone by the time anybody
        asks. Marked, because a reading is a fact about one person and one of them
        is what they pay. -->
        {@const readings = whyReadings(t.why)}
        <div class="redact mt-1 rounded-field bg-base-200/50 px-2 py-1 text-[0.66rem] leading-snug text-base-content/70">
        {#if readings.length}
        <span class="text-base-content/50">read</span>
        {#each readings as r, ri (r.name)}<span
        >{#if ri}<span class="text-base-content/35">, </span>{/if}{r.label}
        <span class="font-medium text-base-content/85">{r.value}</span></span>{/each}
        {:else}
        <span class="text-base-content/50">
        matched with nothing to read — this state catches whoever the ones above it did not
        </span>
        {/if}
        </div>
        {/if}
        </li>
        {/each}
        </ol>
        </div>
        {/if}
        <!-- Timestamps -->
        {#if timestamps.length}
        <div>
        <div class="eyebrow mb-1.5 text-base-content/60">Timestamps</div>
        <dl class="space-y-1">
        {#each timestamps as [rowLabel, iso] (rowLabel)}
        <div class="flex items-baseline justify-between gap-3 text-xs">
        <dt class="text-base-content/60">{rowLabel}</dt>
        <dd class="font-mono tabular-nums text-base-content/78" title={fullDate(iso)}>{timeAgo(iso, nowMs)}</dd>
        </div>
        {/each}
        </dl>
        </div>
        {/if}
        {#if !transitions.length && !timestamps.length}
          <p class="text-xs text-base-content/55">Nothing recorded for this card yet.</p>
        {/if}
      </div>
    {/if}
  </div>

  <!-- THE NOTE, AS THE PANEL'S FOOTER. It sat above the tabs first, which was wrong for
       the reason a long one makes obvious: `whitespace-pre-wrap` on an unbounded note
       pushed the conversation off the bottom of the drawer. Down here it is the last
       thing rather than the first, and the collapsed form is CLAMPED to two lines — the
       editor is one click away for the rest, and a note can no longer cost the pane its
       height however much someone pastes into it. -->
  {#if onNote}
    <div class="shrink-0 border-t hair bg-base-200/30 px-5 py-2">
      {#if noteEditing}
        <!-- privacy-ok: the operator's own working note, mid-edit — the blur belongs on
             the SAVED text below, not on a box somebody is typing into -->
        <textarea
          class="textarea textarea-bordered w-full text-sm leading-relaxed"
          rows="3"
          maxlength="8000"
          placeholder="What do you know about working this person?"
          bind:value={noteDraft}
        ></textarea>
        <div class="mt-1.5 flex items-center gap-2">
          <button class="btn btn-primary btn-xs" onclick={() => void saveNote()}
                  disabled={noteBusy || !noteDirty}>
            {#if noteBusy}<span class="loading loading-spinner loading-xs"></span>{/if}
            Save note
          </button>
          <button class="btn btn-ghost btn-xs" onclick={() => (noteEditing = false)}
                  disabled={noteBusy}>Cancel</button>
          {#if noteError}<span class="text-xs text-error">{noteError}</span>{/if}
        </div>
      {:else}
        <button class="group flex w-full items-start gap-2 text-left" onclick={startNote}
                title={noteSaved || 'Add a note'}>
          <span class="eyebrow shrink-0 pt-0.5 text-base-content/55">Note</span>
          {#if noteSaved}
            <span class="redact line-clamp-2 min-w-0 flex-1 text-xs leading-snug text-base-content/75">{noteSaved}</span>
          {:else}
            <span class="min-w-0 flex-1 text-xs text-base-content/45">
              Add what you know about working this person…
            </span>
          {/if}
          <span class="shrink-0 text-[0.68rem] text-base-content/0 transition-colors group-hover:text-base-content/55">edit</span>
        </button>
      {/if}
    </div>
  {/if}

</Drawer>

{#snippet composer()}
  <!-- Inside the thread, where `<ConversationThread>` pins it under the bubbles — the
       same place the Helper's DM tab and the explorer put theirs. A conversation with
       no messages yet still gets one: that is how a first one starts. -->
  {#key card.member_id}
    <!-- privacy-ok: the placeholder below names the member, and ReplyComposer's
         textarea carries `redact-hint` — which hides a placeholder outright, because a
         placeholder cannot be blurred without blurring what you type into it -->
    <ReplyComposer
      placeholder={`Message ${name}…`}
      {skoolId}
      kind="dm"
      targetId={String(card.member_id)}
      submitLabel="Send"
      doneLabel="Sent"
      submitOnEnter
      autofocus={false}
      onSubmit={sendDm}
    />
  {/key}
{/snippet}
